// ─── Field Condition Engine ────────────────────────────────
// Evaluates visibleWhen / hiddenWhen conditions against live form values.
// Supports logical connectors ($and, $or, $not) and comparison operators
// ($eq, $ne, $gt, $gte, $lt, $lte, $in). Plain key-value pairs use equality.

export type FieldValueComparison =
  | { $eq: unknown }
  | { $ne: unknown }
  | { $gt: number }
  | { $gte: number }
  | { $lt: number }
  | { $lte: number }
  | { $in: unknown[] };

export type FieldConditionLeaf = Record<string, unknown>;

export type FieldCondition =
  | { $and: FieldCondition[] }
  | { $or: FieldCondition[] }
  | { $not: FieldCondition }
  | FieldConditionLeaf;

function compareValue(actual: unknown, expected: unknown): boolean {
  if (expected !== null && typeof expected === 'object' && !Array.isArray(expected)) {
    const exp = expected as Record<string, unknown>;
    if ('$eq' in exp) return actual === exp.$eq;
    if ('$ne' in exp) return actual !== exp.$ne;
    if ('$gt' in exp) return typeof actual === 'number' && actual > (exp.$gt as number);
    if ('$gte' in exp) return typeof actual === 'number' && actual >= (exp.$gte as number);
    if ('$lt' in exp) return typeof actual === 'number' && actual < (exp.$lt as number);
    if ('$lte' in exp) return typeof actual === 'number' && actual <= (exp.$lte as number);
    if ('$in' in exp) return Array.isArray(exp.$in) && (exp.$in as unknown[]).includes(actual);
  }
  return actual === expected;
}

export function evaluateCondition(
  condition: FieldCondition,
  formValues: Record<string, unknown>,
): boolean {
  if ('$and' in condition) {
    return (condition.$and as FieldCondition[]).every((c) => evaluateCondition(c, formValues));
  }
  if ('$or' in condition) {
    return (condition.$or as FieldCondition[]).some((c) => evaluateCondition(c, formValues));
  }
  if ('$not' in condition) {
    return !evaluateCondition(condition.$not as FieldCondition, formValues);
  }
  return Object.entries(condition).every(([fieldKey, expected]) =>
    compareValue(formValues[fieldKey], expected),
  );
}

export function extractDependencyFields(condition: FieldCondition): string[] {
  if ('$and' in condition) {
    return (condition.$and as FieldCondition[]).flatMap(extractDependencyFields);
  }
  if ('$or' in condition) {
    return (condition.$or as FieldCondition[]).flatMap(extractDependencyFields);
  }
  if ('$not' in condition) {
    return extractDependencyFields(condition.$not as FieldCondition);
  }
  return Object.keys(condition);
}
